import { useEffect, useState } from "react";
import { AlertTriangle, Chrome, Copy, Eye, EyeOff, KeyRound, Link2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STORE_URL = "https://chromewebstore.google.com/detail/dpgeglehccekblhcghkengajjodiofjd";

const Step = ({ n, title, children }) => (
  <div className="flex gap-3">
    <span className="w-7 h-7 rounded-full bg-emerald-900 text-white text-sm flex items-center justify-center shrink-0 font-bold">{n}</span>
    <div className="text-sm"><p className="font-bold mb-1">{title}</p><div className="text-slate-500 leading-6">{children}</div></div>
  </div>
);

export default function ExtensionTab() {
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [imports, setImports] = useState([]);
  const [linked, setLinked] = useState(false);
  const serverUrl = process.env.REACT_APP_BACKEND_URL;

  const loadImports = () => api.get("/attendance/imports").then((r) => setImports(r.data)).catch(() => {});
  useEffect(() => { loadImports(); }, []);

  useEffect(() => {
    api.get("/auth/extension-token").then((r) => setToken(r.data.token)).catch(() => {});
    const onLinked = () => setLinked(true);
    window.addEventListener("rasid-linked", onLinked);
    return () => window.removeEventListener("rasid-linked", onLinked);
  }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`تم نسخ ${label}`));
  };

  const revealToken = async () => {
    if (token) { setShowToken(!showToken); return; }
    try {
      const { data } = await api.get("/auth/extension-token");
      setToken(data.token);
      setShowToken(true);
    } catch { toast.error("تعذر إنشاء الرمز"); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="extension-tab">
      <div className="bg-white rounded-2xl border shadow-sm p-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-900 flex items-center justify-center">
            <Chrome className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h3 className="font-bold">إضافة كروم — مستورد حضوري</h3>
            <p className="text-xs text-slate-400">تسحب بيانات الحضور من صفحة حضوري المفتوحة وترسلها هنا</p>
          </div>
        </div>

        <div className="space-y-5">
          <Step n={1} title="تحميل الإضافة وتثبيتها">
            افتح صفحة الأداة في سوق كروم الإلكتروني، ثم اضغط <b>"إضافة إلى Chrome"</b> — يتم التثبيت مباشرة بدون أي خطوات إضافية.
            <div>
              <a href={STORE_URL} target="_blank" rel="noreferrer" data-testid="btn-download-extension"
                className="mt-3 inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-950 text-white font-bold rounded-xl px-6 py-3 text-sm shadow-lg shadow-emerald-900/20 transition-transform hover:-translate-y-0.5">
                <Chrome className="w-5 h-5 text-amber-400" /> حمّل أداة الكروم
              </a>
            </div>
          </Step>
          <Step n={2} title="تسجيل الدخول في الإضافة">
            اضغط أيقونة الإضافة في شريط أدوات كروم، وسجّل الدخول <b>بنفس بريدك الإلكتروني وكلمة مرورك</b> في هذه المنصة — مرة واحدة فقط وتبقى محفوظة في المتصفح.
            {linked && (
              <p className="mt-2 text-xs font-bold text-emerald-700" data-testid="extension-auto-linked">
                تم ربط الإضافة تلقائياً بهذا المتصفح ✓
              </p>
            )}
            <details className="mt-3" data-testid="manual-token-fallback">
              <summary className="text-xs text-slate-400 cursor-pointer">خيار احتياطي: النسخ اليدوي للرابط والرمز (للإصدارات الأقدم من 1.6.0)</summary>
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2 bg-slate-50 border rounded-lg px-3 py-2">
                  <Link2 className="w-4 h-4 text-slate-400 shrink-0" />
                  <code dir="ltr" className="text-xs flex-1 truncate">{serverUrl}</code>
                  <button onClick={() => copy(serverUrl, "رابط الخادم")} data-testid="btn-copy-server-url">
                    <Copy className="w-4 h-4 text-emerald-800" />
                  </button>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 border rounded-lg px-3 py-2">
                  <KeyRound className="w-4 h-4 text-slate-400 shrink-0" />
                  <code dir="ltr" className="text-xs flex-1 truncate" data-testid="extension-token-value">
                    {showToken && token ? token : "••••••••••••••••••••••••••••••"}
                  </code>
                  <button onClick={revealToken} data-testid="btn-reveal-token">
                    {showToken ? <EyeOff className="w-4 h-4 text-emerald-800" /> : <Eye className="w-4 h-4 text-emerald-800" />}
                  </button>
                  {token && (
                    <button onClick={() => copy(token, "الرمز")} data-testid="btn-copy-token">
                      <Copy className="w-4 h-4 text-emerald-800" />
                    </button>
                  )}
                </div>
              </div>
            </details>
            {token && serverUrl && (
              <div id="rasid-bridge" data-server-url={serverUrl} data-token={token}
                style={{ display: "none" }} data-testid="rasid-bridge" />
            )}
          </Step>
          <Step n={3} title="الجلب من حضوري">
            في منصة حضوري من تبويب التقارير اختر تقرير الحضور اليومي، ثم اضغط أيقونة الإضافة وحدد تاريخي
            البداية والنهاية واضغط <b>"جلب الفترة واستيرادها دفعة واحدة"</b> — ستتنقل الأداة تلقائياً بين الأيام
            (متخطية الجمعة والسبت) وتجمع الحضور وقائمة الغير مسجلين ثم تستوردها دفعة واحدة.
            <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5"
              data-testid="hudoori-stay-alert">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-700" />
              <p className="text-[11px] font-bold text-amber-800 leading-5">
                تنبيه: لا تغادر صفحة منصة حضوري حتى تنتهي الأداة من استيراد جميع السجلات وتظهر رسالة باللون الأخضر "تم استيراد السجلات".
              </p>
            </div>
          </Step>
        </div>

        <div className="border-t pt-4">
          <a href="/privacy" target="_blank" rel="noreferrer" data-testid="link-extension-privacy"
            className="flex items-center gap-1.5 text-xs text-emerald-800 hover:text-emerald-950 font-semibold">
            <ShieldCheck className="w-4 h-4" /> سياسة خصوصية الأداة — ما البيانات التي تجمعها وكيف تستخدمها؟
          </a>
        </div>
      </div>

      <div className="bg-white rounded-2xl border shadow-sm p-6">
        <h3 className="font-bold mb-4">سجل عمليات الاستيراد</h3>
        <div className="space-y-2" data-testid="imports-log-list">
          {imports.length === 0 && <p className="text-sm text-slate-400">لا توجد عمليات استيراد بعد</p>}
          {imports.map((imp) => (
            <div key={imp.id} className="flex items-center justify-between border rounded-xl px-4 py-3">
              <div>
                <p className="text-sm font-semibold">
                  {imp.source === "chrome-extension" ? "إضافة كروم" : imp.source === "simulator" ? "استيراد تجريبي" : "بيانات أولية"}
                </p>
                <p className="text-[11px] text-slate-400" dir="ltr">{new Date(imp.created_at).toLocaleString("ar-SA")}</p>
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-emerald-800">{imp.imported} سجل</p>
                {imp.skipped > 0 && <p className="text-[11px] text-amber-600">تم تخطي {imp.skipped}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
